
































































































































































import Vue from 'vue';
import { required, sameAs, maxLength } from 'vuelidate/lib/validators';
import { zip } from '@/validations';
import InputMaskTypes from '@/directives/input-mask/input-mask-types';
import {
  CloseFullscreenWrapper,
  FormValidation,
  OpenHelp,
  StateOptions,
  LoadingMessage,
  TriggerRefreshError,
} from '@/mixins';
import { ValidateAddress } from '@/components/validate-address-component';
import {
  FullscreenWrapper,
  FullscreenSuccess,
  FullscreenError,
  FullscreenLoading,
} from '@/components/fullscreen-wrapper-components';
import { EventBus } from '@/event-bus';
import { ExtendMixins } from '@/services';
import { AddProviderFormModel } from '@/@types/member';
import DataTableSync from 'datatablesync/src/DataTableSync.class';
import { isNil } from 'lodash';

interface Data {
  newProviderId: string;
  validateAddressIsLoading: boolean;
  showNoMatches: boolean;
  loadingMessage: string;
  isLoading: boolean;
  showSuccessMessage: boolean;
  successMessage: string;
  showErrorMessage: boolean;
  errorMessage: string;
  refreshError: boolean;
  confirmBeforeClose: boolean;
  providerModel: AddProviderFormModel;
}

export default ExtendMixins(
  CloseFullscreenWrapper,
  FormValidation,
  OpenHelp,
  StateOptions,
  LoadingMessage,
  TriggerRefreshError,
).extend({
  name: 'AddProvider',
  props: {
    params: {
      type: Object as () => any,
      required: true,
    },
  },
  data(): Data {
    return {
      newProviderId: null,
      validateAddressIsLoading: false,
      showNoMatches: false,
      loadingMessage: this.$options.filters.t([
        'add-provider_text_loading-message_validating-address',
        'provider_text_loading-message_validating-address',
      ]),
      isLoading: false,
      showSuccessMessage: false,
      successMessage: this.params.successMessage,
      showErrorMessage: false,
      errorMessage: this.params.errorMessage,
      refreshError: false,
      confirmBeforeClose: true,
      providerModel: {
        name: '',
        address: {
          addressLine1: '',
          addressLine2: '',
          city: '',
          state: '',
          zipCode: '',
        },
        accountNumber: '',
        confirmAccountNumber: '',
      },
    };
  },
  beforeMount() {
    this.constructShowErrorModel('providerModel');
  },
  validations: {
    providerModel: {
      name: {
        required,
        maxLength: maxLength(40),
      },
      address: {
        addressLine1: {
          required,
          maxLength: maxLength(35),
        },
        addressLine2: {
          maxLength: maxLength(35),
        },
        city: {
          required,
          maxLength: maxLength(25),
        },
        state: {
          required,
        },
        zipCode: {
          required,
          zip,
        },
      },
      accountNumber: {
        required,
        maxLength: maxLength(20),
      },
      confirmAccountNumber: {
        required,
        sameAs: sameAs('accountNumber'),
      },
    },
  },
  computed: {
    dataTableSync(): DataTableSync {
      return this.$store.getters.currentDataTableSyncInstance(
        this.params.dataTableRef,
      );
    },
    //      VALIDATIONS
    nameErrors(): string[] {
      const formField = 'providerModel.name';
      return this.FormValidationMixin.constructErrors(formField, [
        this.FormValidationMixin.validateField(
          formField,
          [
            'provider_form-error_provider-name_required',
            'global_form-error_default_required',
          ],
          'required',
        ),
        this.FormValidationMixin.validateField(
          formField,
          'provider_form-error_provider-name_length',
          'maxLength',
          { key: 'maxLength', val: 'max' },
        ),
      ]);
    },
    addressLine1Errors(): string[] {
      const formField = 'providerModel.address.addressLine1';
      return this.FormValidationMixin.constructErrors(formField, [
        this.FormValidationMixin.validateField(
          formField,
          [
            'provider_form-error_addr-1_required',
            'global_form-error_addr-1-required',
            'global_form-error_default_required',
          ],
          'required',
        ),
        this.FormValidationMixin.validateField(
          formField,
          'provider_form-error_addr-1_length',
          'maxLength',
          { key: 'maxLength', val: 'max' },
        ),
      ]);
    },
    addressLine2Errors(): string[] {
      const formField = 'providerModel.address.addressLine2';
      return this.FormValidationMixin.constructErrors(formField, [
        this.FormValidationMixin.validateField(
          formField,
          'provider_form-error_addr-2_length',
          'maxLength',
          { key: 'maxLength', val: 'max' },
        ),
      ]);
    },
    cityErrors(): string[] {
      const formField = 'providerModel.address.city';
      return this.FormValidationMixin.constructErrors(formField, [
        this.FormValidationMixin.validateField(
          formField,
          [
            'provider_form-error_city_required',
            'global_form-error_city-required',
            'global_form-error_default_required',
          ],
          'required',
        ),
        this.FormValidationMixin.validateField(
          formField,
          'provider_form-error_city_length',
          'maxLength',
          { key: 'maxLength', val: 'max' },
        ),
      ]);
    },
    stateErrors(): string[] {
      const formField = 'providerModel.address.state';
      return this.FormValidationMixin.constructErrors(formField, [
        this.FormValidationMixin.validateField(
          formField,
          [
            'provider_form-error_state_required',
            'global_form-error_state-required',
            'global_form-error_default_required',
          ],
          'required',
        ),
      ]);
    },
    zipCodeErrors(): string[] {
      const formField = 'providerModel.address.zipCode';
      return this.FormValidationMixin.constructErrors(formField, [
        this.FormValidationMixin.validateField(
          formField,
          [
            'provider_form-error_zip_required',
            'global_form-error_zip-required',
            'global_form-error_default_required',
          ],
          'required',
        ),
        this.FormValidationMixin.validateField(
          formField,
          [
            'provider_form-error_zip_invalid-format',
            'global_form-error_default_invalid-format',
          ],
          'zip',
        ),
      ]);
    },
    accountNumberErrors(): string[] {
      const formField = 'providerModel.accountNumber';
      return this.FormValidationMixin.constructErrors(formField, [
        this.FormValidationMixin.validateField(
          formField,
          [
            'provider_form-error_account-number_required',
            'global_form-error_account-number-required',
            'global_form-error_default_required',
          ],
          'required',
        ),
        this.FormValidationMixin.validateField(
          formField,
          'provider_form-error_account-number_length',
          'maxLength',
          { key: 'maxLength', val: 'max' },
        ),
      ]);
    },
    confirmAccountNumberErrors(): string[] {
      const formField = 'providerModel.confirmAccountNumber';
      return this.FormValidationMixin.constructErrors(formField, [
        this.FormValidationMixin.validateField(
          formField,
          [
            'provider_form-error_confirm-account-number_required',
            'global_form-error_confirm-account-number-required',
            'global_form-error_default_required',
          ],
          'required',
        ),
        this.FormValidationMixin.validateField(
          formField,
          ['provider_form-error_confirm-account-number_same-as'],
          'sameAs',
        ),
      ]);
    },
  },
  methods: {
    tryAgain(): void {
      this.showErrorMessage = false;
      this.confirmBeforeClose = true;
    },
    deconstructAccountNumber(): void {
      this.providerModel.accountNumber = this.providerModel.accountNumber.toUpperCase();
    },
    deconstructConfirmAccountNumber(): void {
      this.providerModel.confirmAccountNumber = this.providerModel.confirmAccountNumber.toUpperCase();
    },
    constructPayload() {
      return {
        name: this.providerModel.name
          ? this.providerModel.name.toUpperCase()
          : null,
        address: {
          addressLine1: this.providerModel.address.addressLine1
            ? this.providerModel.address.addressLine1.toUpperCase()
            : null,
          addressLine2: this.providerModel.address.addressLine2
            ? this.providerModel.address.addressLine2.toUpperCase()
            : null,
          city: this.providerModel.address.city
            ? this.providerModel.address.city.toUpperCase()
            : null,
          state: this.providerModel.address.state
            ? this.providerModel.address.state.toUpperCase()
            : null,
          zipCode: this.providerModel.address.zipCode
            ? InputMaskTypes.zip
                .deconstruct(this.providerModel.address.zipCode)
                .toUpperCase()
            : null,
        },
        accountNumber: this.providerModel.accountNumber
          ? this.providerModel.accountNumber.toUpperCase()
          : null,
      };
    },
    validateAddress(): void {
      this.validateAddressIsLoading = true;
      this.isLoading = true;
      this.showNoMatches = false;
    },
    addressCancel(): void {
      this.isLoading = false;
      this.validateAddressIsLoading = false;
    },
    showNoMatchesText(): void {
      this.isLoading = false;
      this.validateAddressIsLoading = false;
      this.showNoMatches = true;
    },
    addProvider(address): void {
      this.validateAddressIsLoading = false;

      if (address) {
        Object.assign(this.providerModel.address, address);
      }

      this.$nextTick(() => {
        this.changeLoadingMessage(
          (this.$refs.fullscreenLoadingComponent as any).$refs.loadingMessage,
          'loadingMessage',
          this.$options.filters.t([
            'add-provider_text_loading-message',
            'provider_text_loading-message',
            'global_loading',
          ]),
        );
      });

      this.$store
        .dispatch('addProvider', this.constructPayload())
        .then((data) => {
          this.newProviderId = data.providerId;
          this.refreshProviders();

          // GTM Event - Add Provider
          window.dataLayer.push({
            event: 'addProvider',
          });
        })
        .catch(() => {
          this.confirmBeforeClose = false;
          this.showErrorMessage = true;
          this.isLoading = false;
        });
    },
    async refreshProviders(): Promise<void> {
      try {
        if (!isNil(this.dataTableSync)) {
          await this.dataTableSync.refresh();
        } else {
          await this.$store.dispatch('getProviders');
        }
        this.confirmBeforeClose = false;
        this.showSuccessMessage = true;
        this.isLoading = false;
        EventBus.$emit('refreshProviders', {
          providerId: this.newProviderId,
        });
      } catch (error) {
        this.isLoading = false;
        this.triggerRefreshError({
          message: [
            'add-provider_text_refresh-data-error-message',
            'global_connection-error',
          ],
        });
      }
    },
  },
  components: {
    FullscreenWrapper,
    FullscreenSuccess,
    FullscreenError,
    FullscreenLoading,
    ValidateAddress,
  },
});
